import "antd/dist/antd.css";
import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { Tooltip, PieChart, Pie, Cell } from "recharts";
import { getOverviewData, getDetailedData, getSelectedData, CustomTooltip } from "../Logics/summaryHelper";
import { overviewColors, retrainingColors, productivityColors, systemColors } from "./PieHelper";
import { Row, Col, Typography } from "antd";
import "./CostPies.css";

const { Title } = Typography;
const lodash = require("lodash");

const titles = ["Retraining Costs", "Productivity Loss Costs", "System Change Costs"];
const detailedRetraining = [
  "Coder",
  "Data Analyst",
  "Manager",
  "Data Quality Specialist",
  "System Application Coordinator",
  "Coding Director",
  "Coding Coordinator",
  "Project Coordinator",
  "Supervisor",
  "Data Entry Clerk",
  "College Instructor",
  "Provincial Trainer",
  "Other",
];
const detailedProductivity = ["Productivity Loss"];
const detailedSystem = ["IT Cost", "IT Personnel Cost", "Other System Costs"];

const CostPies = (props) => {
  const [selectedIndex, updateSelectedIndex] = useState();

  const adjustment = useMemo(() => {
    const adjustment = Math.pow(
      parseFloat(props.costAdjustDetails["inflation-rate"]) / 100 + 1,
      parseFloat(props.costAdjustDetails["years-into-future"])
    );
    if (!adjustment || isNaN(adjustment)) {
      return 1;
    }
    return adjustment;
  }, [props.costAdjustDetails]);

  const overviewData = useMemo(
    () => getOverviewData(adjustment, props.breakInMode),
    [
      adjustment,
      props.retrainCostDetails,
      props.prodLossDetails,
      props.systemCostDetails,
      props.costAdjustDetails,
      props.breakInMode,
    ]
  );

  const detailedData = useMemo(
    () => getDetailedData(adjustment, props.breakInMode),
    [
      adjustment,
      props.retrainCostDetails,
      props.prodLossDetails,
      props.systemCostDetails,
      props.costAdjustDetails,
      props.breakInMode,
    ]
  );

  const selectedData = useMemo(
    () => getSelectedData(adjustment, props.breakInMode, selectedIndex),
    [
      adjustment,
      props.retrainCostDetails,
      props.prodLossDetails,
      props.systemCostDetails,
      props.costAdjustDetails,
      props.breakInMode,
      selectedIndex,
    ]
  );

  const renderCustomizedLabelOutside = ({ cx, cy, midAngle, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.05;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    var text = "";
    if ((percent > 0.03 && props.width > 1000) || percent > 0.06) {
      text = detailedData[index].name;
    }

    //Use of <text> throws warnings in both Edge and Firefox, but the options below actaully work (e.g. fill="green" makes the text green). Replacing with a <div> breakes things.
    return (
      <text x={x} y={y} fill="black" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={12}>
        {text}
      </text>
    );
  };

  const renderCustomizedLabelInside = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.2;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    var text = "";
    if ((percent > 0.15 && props.width > 1200) || (percent > 0.5 && props.width > 800)) {
      text = overviewData[index].name;
    }

    return (
      <text x={x} y={y} fill="black" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={12}>
        {text}
      </text>
    );
  };

  const renderCustomizedLabelSelected = ({ cx, cy, midAngle, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.05;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    var text = "";
    if (percent > 0.03) {
      text = selectedData[index].name;
    }

    return (
      <text x={x} y={y} fill="black" textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={12}>
        {text}
      </text>
    );
  };

  const shouldHidePie = () => !overviewData[0].value && !overviewData[1].value && !overviewData[2].value;

  const shouldHideDetailPie = () =>
    lodash.isEmpty(selectedData[0]) && (overviewData[0].value || overviewData[1].value || overviewData[2].value);

  const pieWidth = () => {
    if (props.width >= 1200) {
      return props.width / 2 - 17;
    } else {
      return props.width - 17;
    }
  };

  const getDetailedColor = (entry) => {
    if (detailedRetraining.includes(entry.name)) {
      return retrainingColors[detailedRetraining.indexOf(entry.name)];
    } else if (detailedProductivity.includes(entry.name)) {
      return productivityColors[detailedProductivity.indexOf(entry.name)];
    } else {
      // system costs
      return systemColors[detailedSystem.indexOf(entry.name)];
    }
  };

  const renderDetailPie = () => {
    if (shouldHideDetailPie()) {
      return <Title level={4}>Click on a pie slice to view a detailed breakdown</Title>;
    } else {
      return (
        <>
          <Title level={4}>{titles[selectedIndex]}</Title>
          <PieChart width={pieWidth()} height={props.width * 0.32}>
            <Pie
              blendStroke
              data={selectedData}
              dataKey="value"
              nameKey={"selectedPie"}
              cx="50%"
              cy="50%"
              outerRadius={props.width * 0.12}
              labelLine={false}
              label={renderCustomizedLabelSelected}
              isAnimationActive={false}
            >
              {selectedData.map((entry, index) => (
                <Cell key={"cell-selectedPie" + index} fill={getDetailedColor(entry)} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />}></Tooltip>
          </PieChart>
        </>
      );
    }
  };

  if (shouldHidePie()) {
    return (
      <div style={{ height: 256, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Title level={4}>No data to display</Title>
      </div>
    );
  }

  return (
    <Row type="flex" justify="center" align="middle">
      <Col xs={24} sm={24} md={24} lg={24} xl={12} style={{ marginTop: 20 }}>
        <Title level={4}>Cost Breakdown</Title>
        <PieChart width={pieWidth()} height={props.width * 0.32}>
          <Pie
            blendStroke
            data={overviewData}
            dataKey="value"
            nameKey={"overviewPie"}
            cx="50%"
            cy="50%"
            outerRadius={props.width * 0.1}
            labelLine={false}
            label={renderCustomizedLabelInside}
            onClick={(_, index) => updateSelectedIndex(index)}
            isAnimationActive={false}
          >
            {overviewData.map((_, index) => (
              <Cell key={"cell-overviewPie" + index} fill={overviewColors[index]} />
            ))}
          </Pie>
          <Pie
            blendStroke
            data={detailedData}
            dataKey="value"
            nameKey={"detailedPie"}
            cx="50%"
            cy="50%"
            innerRadius={props.width * 0.11}
            outerRadius={props.width * 0.12}
            labelLine={false}
            label={renderCustomizedLabelOutside}
            isAnimationActive={false}
          >
            {detailedData.map((entry, index) => (
              <Cell key={"cell-detailedPie" + index} fill={getDetailedColor(entry)} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />}></Tooltip>
        </PieChart>
      </Col>
      <Col xs={24} sm={24} md={24} lg={24} xl={12} style={{ marginTop: 20 }}>
        {renderDetailPie()}
      </Col>
    </Row>
  );
};

const mapStateToProps = (state) => {
  return {
    retrainCostDetails: state.costs.retrainCostDetails,
    prodLossDetails: state.costs.prodLossDetails,
    systemCostDetails: state.costs.systemCostDetails,
    costAdjustDetails: state.costs.costAdjustDetails,
  };
};

export default connect(mapStateToProps, null)(CostPies);
